import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ExtractionStatus, 
  ExtractionProgress, 
  DataTypeStatus 
} from '../types/chat';
import { 
  MapPin, 
  Utensils, 
  Cloud, 
  Plane, 
  FileText,
  Loader2,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

interface ExtractionLoaderProps {
  status: ExtractionStatus;
  progress?: ExtractionProgress;
  startTime?: Date;
}

const dataTypeConfig = {
  locations: {
    icon: MapPin,
    label: 'Places',
    loadingMessages: [
      'Finding interesting spots...',
      'Checking top attractions...',
      'Mapping locations...'
    ],
    color: 'text-blue-500'
  },
  restaurant: {
    icon: Utensils,
    label: 'Dining',
    loadingMessages: [
      'Searching great eats...',
      'Checking reviews...',
      'Finding the best spots...'
    ],
    color: 'text-orange-500'
  },
  weather: {
    icon: Cloud,
    label: 'Weather',
    loadingMessages: [
      'Checking conditions...',
      'Getting forecast...',
      'Loading weather data...'
    ],
    color: 'text-sky-500'
  },
  flights: {
    icon: Plane,
    label: 'Flights',
    loadingMessages: [
      'Checking flight status...',
      'Finding connections...',
      'Getting updates...'
    ],
    color: 'text-purple-500'
  },
  visa: {
    icon: FileText,
    label: 'Travel Docs',
    loadingMessages: [
      'Checking requirements...',
      'Finding visa info...',
      'Getting travel rules...'
    ],
    color: 'text-green-500'
  }
};

export const ExtractionLoader: React.FC<ExtractionLoaderProps> = ({ 
  status, 
  progress = {},
  startTime 
}) => {
  const [messageIndex, setMessageIndex] = React.useState(0);
  
  // Rotate loading messages
  React.useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % 3);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Calculate overall progress
  const calculateOverallProgress = () => {
    const dataTypes = Object.keys(progress);
    if (dataTypes.length === 0) return 0;
    
    const completedCount = dataTypes.filter(
      key => progress[key]?.status === DataTypeStatus.COMPLETE
    ).length;
    
    return (completedCount / dataTypes.length) * 100;
  };

  const getStatusIcon = (itemStatus?: DataTypeStatus) => {
    switch (itemStatus) {
      case DataTypeStatus.COMPLETE:
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case DataTypeStatus.ERROR:
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case DataTypeStatus.PROCESSING:
        return <Loader2 className="w-4 h-4 animate-spin" />;
      default:
        return null;
    }
  };

  const getMainMessage = () => {
    switch (status) {
      case ExtractionStatus.DETECTING:
        return 'Searching for info...';
      case ExtractionStatus.EXTRACTING:
        return 'Searching for info...';
      case ExtractionStatus.ENRICHING:
        return 'Getting details...';
      case ExtractionStatus.RENDERING:
        return 'Almost there...';
      default:
        return 'Working on it...';
    }
  };

  if (status === ExtractionStatus.IDLE || status === ExtractionStatus.COMPLETE) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="w-full max-w-2xl mx-auto p-4"
      >
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
          {/* Main Status */}
         {/*  <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-800">
                {getMainMessage()}
              </h3>
              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
            </div>
             */}
            {/* Overall Progress Bar */}
            {/* <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-blue-500 to-blue-600"
                initial={{ width: '0%' }}
                animate={{ width: `${calculateOverallProgress()}%` }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
              />
            </div> */}
         {/*  </div> */}

          {/* Data Type Progress */}
          <div className="space-y-3">
            {Object.entries(progress).map(([dataType, typeProgress]) => {
              const config = dataTypeConfig[dataType];
              if (!config) return null;
              
              const Icon = config.icon;
              const isProcessing = typeProgress.status === DataTypeStatus.PROCESSING;
              
              return (
                <motion.div
                  key={dataType}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center space-x-3"
                >
                  <div className={`p-2 rounded-lg bg-gray-50 ${config.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-700">
                          {config.label}
                        </span>
                        {typeProgress.count > 0 && (
                          <span className="text-sm text-gray-500">
                            ({typeProgress.count} found)
                          </span>
                        )}
                      </div>
                      {getStatusIcon(typeProgress.status)}
                    </div>
                    
                    <p className="text-sm text-gray-600">
                      {isProcessing 
                        ? config.loadingMessages[messageIndex]
                        : typeProgress.message}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Error Message */}
          {status === ExtractionStatus.ERROR && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg"
            >
              <p className="text-sm text-red-600">
                Something went wrong. Please try again.
              </p>
            </motion.div>
          )}

          {/* Elapsed Time */}
          {startTime && (
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500">
                Processing time: {Math.round((Date.now() - startTime.getTime()) / 1000)}s
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ExtractionLoader;